import React, { useState } from 'react';
import styled from 'styled-components';

import { AlarmBESS } from '../../../models/AlarmBESS';
import { AlarmCell } from '../../../models/AlarmCell';
import { AlarmElectropost } from '../../../models/AlarmElectropost';
import { AlarmPCS } from '../../../models/AlarmPCS';
import { AlarmSens } from '../../../models/AlarmSens';
import { AlarmString } from '../../../models/AlarmString';
import { Spinner } from '../../components';
import { FormField } from '../../components/FormField';
import { Title } from '../../components/Title';
import { useSubscription } from '../../scripts/hooks/realtimeHook';
import { useSocket } from '../../scripts/hooks/socketHook';

const STATIC_BASE_URL = import.meta.env.VITE_STATIC_URI;

const StyledSBess = styled.div<{ area: string }>`
  display: grid;
  grid-area: ${(props) => props.area};
  gap: 5px;
  justify-content: center;
  align-items: center;

  &&:hover {
    cursor: pointer;
    /* text-shadow: rgb(0, 0, 0) 2px 2px 0px; */
  }
`;

const Img = styled.img<{ visible: boolean }>`
  display: ${({ visible }) => (visible ? 'flex' : 'none')};
  width: 185px;
  height: 145px;
  padding: 5px;
  filter: grayscale(30%);

  &:hover {
    filter: grayscale(0);
  }
`;

const LoadingContainer = styled.div<{ visible: boolean }>`
  display: ${({ visible }) => (visible ? 'flex' : 'none')};
  width: 155px;
  height: 145px;

  padding: 5px;
  border-radius: 20px;

  justify-content: center;
  align-items: center;
`;

const Subtitle = styled.div`
  display: grid;
  width: max-content;
  padding: 2px 8px 2px 8px;
  border-radius: 20px;
  background-color: ${(props) => props.theme.pallete.border.main};

  margin-left: auto;
  margin-right: auto;
  text-align: center;
  justify-content: center;
`;

export type SBessProps = {
  /**
   * Text to display with bess icon.
   */
  title: string;
  /**
   * Subtitle text to display.
   */
  subtitle?: string;
  /**
   * Fired when the user clicks the bess icon.
   */
  onClick?: () => void;
  /**
   * Grid Area
   */
  area?: string;

  token: string;

  bess: string;
};

/**
 * SBess component as Custom FormField
 */
export const SBess: React.FC<SBessProps> = ({ area = 'unset', title, subtitle, onClick, token, bess }: SBessProps) => {
  const SOCKET_BASE_URL = import.meta.env.VITE_SOCKET_URI;
  const [socket, ,] = useSocket(SOCKET_BASE_URL!, bess, token);
  const [isloading, setIsLoading] = useState(true);

  const alarmcell = useSubscription<AlarmCell>(socket, `alarmcell`);
  const alarmpcs = useSubscription<AlarmPCS>(socket, `alarmpcs`);
  const alarmsens = useSubscription<AlarmSens>(socket, `alarmsens`);
  const alarmstring = useSubscription<AlarmString>(socket, `alarmstring`);
  const alarmbess = useSubscription<AlarmBESS>(socket, `alarmbess`);
  const alarmelectropost = useSubscription<AlarmElectropost>(socket, 'alarmelectropost');

  const AlarmString = Object.values(alarmstring?.Data ?? '');
  const AlarmCell = Object.values(alarmcell?.Data ?? '');
  const AlarmBESS = Object.values(alarmbess?.Data ?? '');
  const AlarmSens = Object.values(alarmsens?.Data ?? '');
  const AlarmPCS = alarmpcs?.Data?.map((alarmpcs) => alarmpcs?.value);
  const AlarmElec = alarmelectropost?.Data?.map((alarmelectropost) => alarmelectropost?.value);

  const Alarms = AlarmBESS.concat(AlarmSens, AlarmCell, AlarmPCS, AlarmElec, AlarmString);

  const hasAlarms = () => {
    if (Alarms.includes('level3')) {
      return `${STATIC_BASE_URL}/bess3.png`;
    } else if (Alarms.includes('level2')) {
      return `${STATIC_BASE_URL}/bess2.png`;
    } else if (Alarms.includes('level1')) {
      return `${STATIC_BASE_URL}/bess1.png`;
    }
    return `${STATIC_BASE_URL}/bess0.png`;
  };
  return (
    <StyledSBess area={area} onClick={() => onClick?.call(this)}>
      <FormField
        txtalign='center'
        area='unset'
        label={title}
        size='medium'
        color='white'
        halign='center'
        halignItems='center'
        valignItems='center'
        invert>
        <Img src={hasAlarms()} visible={!isloading} onLoad={() => setIsLoading(false)} />
        <LoadingContainer visible={isloading}>
          <Spinner icon='Battery' size='large' color='warning' background='dark' />
        </LoadingContainer>
      </FormField>
      {subtitle && (
        <Subtitle>
          <Title area='unset' size='xsmall' color='white' value={subtitle} />
        </Subtitle>
      )}
    </StyledSBess>
  );
};
